import { FC } from "react"
import { Button, Grid, IconButton, Stack, Typography } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/DeleteOutlined"
import {
  AutocompleteElement,
  DatePickerElement,
  SelectElement,
  TextFieldElement,
  useFieldArray,
  useFormContext,
} from "react-hook-form-mui"
import { format } from "date-fns"
import { RtkHookForm } from "@core/components"
import {
  useRealizationsFiltersQuery,
  useCreateBatchMutation,
} from "../../store"
import { Realization } from "../../interfaces"

interface ICourierRow {
  courier: Realization.IdName | null
  issued_date: Date | null
}

interface IFormData {
  product: Realization.Product | null
  city: Realization.IdName | null
  weight: string
  measure: string
  batch_date: Date | null
  note: string
  couriers: ICourierRow[]
}

const MEASURE_OPTIONS = [
  { id: "грамм", label: "граммы" },
  { id: "штук", label: "штуки" },
]

// Подсказка по конвертации веса
const WeightHint: FC = () => {
  const { watch } = useFormContext<IFormData>()
  const raw = watch("weight")
  const measure = watch("measure")
  const g = parseFloat(raw)

  if (measure !== "грамм" || !raw || isNaN(g) || g <= 0) return null

  let hint: string | null = null
  if (g < 1) {
    hint = `= ${(g * 1000).toLocaleString("ru-RU", { maximumFractionDigits: 2 })} мг`
  } else if (g >= 1000) {
    hint = `= ${(g / 1000).toLocaleString("ru-RU", { maximumFractionDigits: 3 })} кг`
  }

  if (!hint) return null

  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ mt: 0.5, display: "block" }}
    >
      {hint}
    </Typography>
  )
}

// Динамический список курьеров, назначаемых при создании партии
const CouriersFields: FC<{
  options: Realization.IdName[]
  loading: boolean
}> = ({ options, loading }) => {
  const { control } = useFormContext<IFormData>()
  const { fields, append, remove } = useFieldArray({
    control,
    name: "couriers",
  })

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Курьеры</Typography>
      {fields.map((field, index) => (
        <Stack
          key={field.id}
          direction="row"
          spacing={1}
          alignItems="flex-start"
        >
          <AutocompleteElement
            name={`couriers.${index}.courier`}
            label="Курьер"
            required
            loading={loading}
            options={options}
            autocompleteProps={{
              fullWidth: true,
              size: "small",
              getOptionLabel: (o: Realization.IdName) => o.name,
              isOptionEqualToValue: (o, v) => o.id === v.id,
            }}
          />
          <DatePickerElement
            name={`couriers.${index}.issued_date`}
            label="Дата выдачи"
            inputProps={{ fullWidth: true, size: "small" }}
          />
          <IconButton
            size="small"
            onClick={() => remove(index)}
            sx={{ mt: 0.5 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Button
        startIcon={<AddIcon />}
        size="small"
        sx={{ alignSelf: "flex-start" }}
        onClick={() => append({ courier: null, issued_date: new Date() })}
      >
        Добавить курьера
      </Button>
    </Stack>
  )
}

interface IProps {
  onSuccess?: () => void
}

export const CreateBatchForm: FC<IProps> = ({ onSuccess }) => {
  const { data: filters, isLoading: filtersLoading } =
    useRealizationsFiltersQuery({})

  return (
    <RtkHookForm
      mutationHook={useCreateBatchMutation}
      formContextParams={{
        defaultValues: {
          product: null,
          city: null,
          weight: "",
          measure: "грамм",
          batch_date: new Date(),
          note: "",
          couriers: [],
        } satisfies IFormData,
      }}
      getSubmitHandlerParams={async (body: IFormData) => {
        const req: Realization.CreateBatchRequest = {
          product_id: body.product?.id ?? 0,
        }
        if (body.city) req.city_id = body.city.id
        if (body.weight) {
          req.weight = Number(body.weight)
          req.measure = body.measure
        }
        if (body.batch_date)
          req.batch_date = format(body.batch_date, "yyyy-MM-dd")
        if (body.note) req.note = body.note
        const couriers = body.couriers
          .filter((row) => row.courier)
          .map((row) => {
            const courier: Realization.AddCourierToBatchRequest = {
              staff_id: row.courier!.id,
            }
            if (row.issued_date)
              courier.issued_date = format(row.issued_date, "yyyy-MM-dd")
            return courier
          })
        if (couriers.length) req.couriers = couriers
        return { body: req }
      }}
      afterSubmit={() => {
        onSuccess?.()
      }}
      actions={[{ text: "Создать", type: "submit", variant: "outlined" }]}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <AutocompleteElement
            name="product"
            label="Продукт"
            required
            loading={filtersLoading}
            options={filters?.products ?? []}
            autocompleteProps={{
              fullWidth: true,
              getOptionLabel: (o: Realization.Product) => o.name,
              isOptionEqualToValue: (o, v) => o.id === v.id,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <AutocompleteElement
            name="city"
            label="Город"
            loading={filtersLoading}
            options={filters?.cities ?? []}
            autocompleteProps={{
              fullWidth: true,
              getOptionLabel: (o: Realization.IdName) => o.name,
              isOptionEqualToValue: (o, v) => o.id === v.id,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextFieldElement
            name="weight"
            label="Кол-во"
            type="number"
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
          />
          <WeightHint />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <SelectElement
            name="measure"
            label="Ед. изм."
            fullWidth
            options={MEASURE_OPTIONS}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DatePickerElement
            name="batch_date"
            label="Дата партии"
            inputProps={{ fullWidth: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextFieldElement
            name="note"
            label="Примечание"
            multiline
            rows={2}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <CouriersFields
            options={filters?.couriers ?? []}
            loading={filtersLoading}
          />
        </Grid>
      </Grid>
    </RtkHookForm>
  )
}
